import { useCIQMutation, useCIQQuery } from "hooks/ciq-gql-hooks";
import { useContext, useEffect, useRef, useState } from "react";
import { ProjectContext } from "context/ProjectProvider";
import { gql } from "@apollo/client";
import { MUTATION_CREATE_FEATURE_INSTANCE } from "entity-app/graphQL/ciq-feature-mutations";
import { message } from "utils/ciq-message";
import { ExtractSpecCardItem, MaterialExtractSpecType } from "../model";
import {
  QUERY_EXTRACTION_HISTORY_DATA,
  QUERY_MATERIAL_EXTRACTED_DATA
} from "../queries";

export const useMaterialLogData = () => {
  const {
    gqlClientForProject,
    eventLogs,
    columnHeaders: { materialHeaderMap }
  } = useContext(ProjectContext);

  const [entityList, setEntityList] =
    useState<Array<MaterialExtractSpecType>>();
  const [entityListLoading, setEntityListLoading] = useState(false);
  const [entityListLoadedOnce, setEntityListLoadedOnce] = useState(false);

  const {
    data: materialExtractionData,
    refetch: refetchMaterialExtractionData
  } = useCIQQuery<{
    extraction_data: Array<MaterialExtractSpecType>;
  }>(QUERY_MATERIAL_EXTRACTED_DATA, {
    client: gqlClientForProject,
    skip: !gqlClientForProject
  });

  const { data: extractionHistoryData, refetch: refetchExtractionHistoryData } =
    useCIQQuery<{
      extraction_data_progress_func: Array<{
        projectId: string;
        status_wise_extraction_data: Array<ExtractSpecCardItem>;
      }>;
    }>(QUERY_EXTRACTION_HISTORY_DATA, {
      client: gqlClientForProject,
      skip: !gqlClientForProject
    });

  useEffect(() => {
    if (materialExtractionData && materialExtractionData.extraction_data) {
      setEntityList(materialExtractionData.extraction_data);
      setEntityListLoadedOnce(true);
      setEntityListLoading(false);
    }
    return () => {};
  }, [materialExtractionData]);

  const previousEventLogs = useRef(eventLogs);
  useEffect(() => {
    if (eventLogs.length && previousEventLogs.current !== eventLogs) {
      if (eventLogs.some((log) => log.data_source === "extraction_data")) {
        refetchMaterialExtractionData();
      }

      if (eventLogs.some((log) => log.data_source === "specifications")) {
        refetchExtractionHistoryData();
      }
    }
    previousEventLogs.current = eventLogs;
  }, [eventLogs, refetchMaterialExtractionData, refetchExtractionHistoryData]);

  const [createEntityMutation] = useCIQMutation(
    gql(MUTATION_CREATE_FEATURE_INSTANCE),
    {
      client: gqlClientForProject
    }
  );

  const addMaterialsInLog = async (
    selectedRows: MaterialExtractSpecType[],
    overrides?: Array<{
      description?: string;
      spec_sections?: string[];
      responsible_contractor?: string;
      assignee?: string;
      gc_representative?: string;
      tag_name?: string;
      title?: string;
    }>
  ) => {
    if (!selectedRows) return;

    const objects = selectedRows.map((m, i) => {
      const extra = overrides?.[i] || {};
      return {
        extraction_data_id: m.id,
        title: extra.title || m.title,
        tag_name: extra.tag_name || m.tag,
        manufacturer: m.manufacturer,
        ...(extra.description && { description: extra.description }),
        ...(extra.spec_sections?.length && {
          spec_sections: extra.spec_sections
        }),
        ...(extra.responsible_contractor && {
          responsible_contractor: extra.responsible_contractor
        }),
        ...(extra.assignee && { assignee: extra.assignee }),
        ...(extra.gc_representative && {
          gc_representative: extra.gc_representative
        })
      };
    });

    if (objects && objects?.length) {
      const variables = {
        feature_type: "MATERIALS",
        feature_instances: objects
      };
      const creatResponse: any = await createEntityMutation({
        variables
      });

      if (creatResponse?.data?.insert_feature_instances_multi?.ids?.length) {
        const numberOfMaterial =
          creatResponse?.data?.insert_feature_instances_multi?.ids?.length;
        message.success(`${numberOfMaterial} materials were added to the log.`);
        // refetch();
      } else {
        message.error("Unable to create Material.");
      }
    }
  };

  return {
    entityList,
    setEntityList,
    entityListLoading,
    entityListLoadedOnce,
    materialHeaderMap,
    addMaterialsInLog,
    extractionHistoryData: extractionHistoryData?.extraction_data_progress_func
      ?.length
      ? extractionHistoryData?.extraction_data_progress_func[0]
          ?.status_wise_extraction_data
      : null,
    refetchExtractionHistoryData
  };
};
